import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';

export type TrackChangesAction = 'enable' | 'apply_anyway' | 'cancel';

interface TrackChangesPromptProps {
  open: boolean;
  onResolve: (action: TrackChangesAction, dontAskAgain: boolean) => void;
}

const SIGNUS_BLUE = '#1014f5';
const SIGNUS_BLUE_HOVER = '#2a2eff';
const SIGNUS_BLUE_PRESSED = '#0a0dcc';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  // Sizing/shape only — no color, so it never fights the primary's white text.
  button: {
    minWidth: 'auto',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    '& *': { whiteSpace: 'nowrap' },
  },
  // Brand-blue text for the secondary buttons (Cancel / Apply anyway).
  secondaryText: {
    color: SIGNUS_BLUE,
    ':hover': { color: SIGNUS_BLUE_HOVER },
    ':hover:active': { color: SIGNUS_BLUE_PRESSED },
  },
  // Primary "Enable & apply" — solid Signus blue with white text in every state.
  primary: {
    backgroundColor: SIGNUS_BLUE,
    border: `1px solid ${SIGNUS_BLUE}`,
    color: '#ffffff',
    ':hover': {
      backgroundColor: SIGNUS_BLUE_HOVER,
      border: `1px solid ${SIGNUS_BLUE_HOVER}`,
      color: '#ffffff',
    },
    ':hover:active': {
      backgroundColor: SIGNUS_BLUE_PRESSED,
      border: `1px solid ${SIGNUS_BLUE_PRESSED}`,
      color: '#ffffff',
    },
  },
});

const TrackChangesPrompt: React.FC<TrackChangesPromptProps> = ({ open, onResolve }) => {
  const styles = useStyles();
  const [dontAskAgain, setDontAskAgain] = useState(false);

  // Reset the checkbox each time the dialog re-opens.
  useEffect(() => {
    if (open) setDontAskAgain(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onResolve('cancel', dontAskAgain);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Track Changes is off</DialogTitle>
          <DialogContent className={styles.content}>
            <span>Enable Track Changes before applying this rewrite?</span>
            <Checkbox
              label="Don't ask again for this document"
              checked={dontAskAgain}
              onChange={(_, data) => setDontAskAgain(data.checked === true)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              className={mergeClasses(styles.button, styles.secondaryText)}
              onClick={() => onResolve('cancel', dontAskAgain)}
            >
              Cancel
            </Button>
            <Button
              className={mergeClasses(styles.button, styles.secondaryText)}
              onClick={() => onResolve('apply_anyway', dontAskAgain)}
            >
              Apply anyway
            </Button>
            <Button
              className={mergeClasses(styles.button, styles.primary)}
              appearance="primary"
              onClick={() => onResolve('enable', dontAskAgain)}
            >
              Enable &amp; apply
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default TrackChangesPrompt;
